"use client";

import type { AiFrontendToolResult } from "@/lib/ai/context";
import {
	findContainedEntityAlias,
	normalizeEntityAliasInput,
	resolveEntityAliasExact,
	type EntityAliasMatch,
} from "@/lib/entity-aliases";
import {
	searchAddressMarksInStorage,
	type AddressMarkSearchMatch,
} from "@/lib/search/address-mark-search";

export const PRINCIPAL_REGEX_FLEXIBLE = /^[a-z0-9]{3,6}(-[a-z0-9]{3,6}){10,}$/i;
export const BTC_ADDRESS_REGEX =
	/^(bc1p[a-z0-9]{38,60}|bc1q[a-z0-9]{38,60}|bc1[a-z0-9]{39,87}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/i;
export const EVM_ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;
const PRINCIPAL_REGEX_EXTRACT = /[a-z0-9]{3,6}(?:-[a-z0-9]{3,6}){10,}/gi;
const BTC_ADDRESS_REGEX_EXTRACT =
	/(?:bc1p[a-z0-9]{38,60}|bc1q[a-z0-9]{38,60}|bc1[a-z0-9]{39,87}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})/gi;
const ADDRESS_QUERY_FILLER_PATTERNS = [
	/请问/g,
	/帮我/g,
	/看看/g,
	/看下/g,
	/查下/g,
	/查查/g,
	/分析一下/g,
	/分析下/g,
	/说说/g,
	/讲讲/g,
	/给我/g,
	/关于/g,
	/这个/g,
	/那个/g,
	/地址/g,
	/账户/g,
	/账号/g,
	/用户/g,
	/人/g,
	/号/g,
	/情况/g,
	/画像/g,
	/\bplease\b/gi,
	/\bshow\b/gi,
	/\banalyze\b/gi,
	/\babout\b/gi,
];
const LATIN_SEGMENT_RE = /\b[a-z0-9][a-z0-9._-]{1,63}\b/gi;
const CJK_SEGMENT_RE = /[\u4e00-\u9fff\u3400-\u4dbf\u3007\u03c0π]{2,24}/g;
const SELF_ADDRESS_REFERENCE_RE =
	/(^我的$|我的?(地址|账户|账号|principal|钱包)|我自己(的)?(地址|账户|账号|钱包)?|分析下我(的)?(地址|账户|账号|钱包)?|看看我(的)?(地址|账户|账号|钱包)?|\bmy\s+(address|account|wallet)\b|\bmyself\b)/i;

export interface ResolvedAddressEntity {
	entityType: "address";
	entityId: string;
	label: string;
	source:
		| "alias-account"
		| "principal"
		| "btc-address-owner"
		| "address-mark";
	alias?: string;
	matchedText?: string;
}

export interface AddressMessageResolution {
	entity: ResolvedAddressEntity | null;
	toolResult: AiFrontendToolResult | null;
	needsViewerAuth?: boolean;
}

interface ViewerIdentityLike {
	isAuthenticated: boolean;
	authStatus?: "anonymous" | "restoring" | "ready";
	principal?: string;
	btcAddress?: string;
	walletType?: string;
}

interface AddressSearchResponse {
	data?: Array<{
		id: string;
		type?: string;
		entity?: {
			principal?: string;
			btc_deposit_address?: string | null;
			btc_wallet_address?: string | null;
			rune_deposit_address?: string | null;
		} | null;
	}>;
}

type AddressSearchItem = NonNullable<AddressSearchResponse["data"]>[number];

function hasExactAddressMatch(
	entity: AddressSearchItem["entity"] | undefined | null,
	query: string,
) {
	if (!entity) return false;
	const normalized = query.trim().toLowerCase();

	return (
		(entity.btc_wallet_address || "").toLowerCase() === normalized ||
		(entity.btc_deposit_address || "").toLowerCase() === normalized ||
		(entity.rune_deposit_address || "").toLowerCase() === normalized
	);
}

async function fetchAddressOwnerByBtcAddress(btcAddress: string) {
	const response = await fetch(
		`https://api.odin.fun/v1/search?q=${encodeURIComponent(btcAddress)}`,
	);
	if (!response.ok) return null;

	const data = (await response.json()) as AddressSearchResponse;
	return (
		data.data?.find(
			(item) => item.type === "user" && hasExactAddressMatch(item.entity, btcAddress),
		) ?? null
	);
}

function extractFirstRegexMatch(message: string, regex: RegExp) {
	const globalRegex = regex.global
		? regex
		: new RegExp(regex.source, `${regex.flags}g`);
	const matched = message.match(globalRegex);
	return matched?.[0] ?? null;
}

function sanitizeMessageForAddressQueryExtraction(message: string) {
	let sanitized = message.normalize("NFKC");
	for (const pattern of ADDRESS_QUERY_FILLER_PATTERNS) {
		sanitized = sanitized.replace(pattern, " ");
	}

	return sanitized;
}

function extractNormalizedAddressMessageQueries(message: string) {
	const sanitized = sanitizeMessageForAddressQueryExtraction(message);
	const queries = new Set<string>();
	const fullQuery = normalizeEntityAliasInput(sanitized);
	if (fullQuery) {
		queries.add(fullQuery);
	}

	for (const match of sanitized.match(LATIN_SEGMENT_RE) ?? []) {
		const normalized = normalizeEntityAliasInput(match);
		if (normalized.length >= 2) {
			queries.add(normalized);
		}
	}

	for (const match of sanitized.match(CJK_SEGMENT_RE) ?? []) {
		const normalized = normalizeEntityAliasInput(match);
		if (normalized.length >= 2) {
			queries.add(normalized);
		}
	}

	return Array.from(queries).slice(0, 8);
}

function toAddressMarkEntity(match: AddressMarkSearchMatch): ResolvedAddressEntity {
	return {
		entityType: "address",
		entityId: match.address,
		label: match.displayName || match.address,
		source: "address-mark",
		matchedText: match.matchedValue,
	};
}

function buildAddressMarkDisambiguationToolResult(
	query: string,
	matches: AddressMarkSearchMatch[],
): AiFrontendToolResult {
	return {
		toolId: "address_resolution",
		entityKey: `message:${normalizeEntityAliasInput(query)}`,
		status: "ok",
		displayMode: "disambiguation",
		summary: `我找到了多个可能匹配「${query}」的地址标记，请先确认具体对象。`,
		payload: {
			query,
			matches: matches.map((item) => ({
				address: item.address,
				displayName: item.displayName,
				color: item.color,
				tags: item.tags,
				groupNames: item.groupNames,
				matchedField: item.matchedField,
				matchedValue: item.matchedValue,
				matchKind: item.matchKind,
				score: item.score,
			})),
		},
		fetchedAt: new Date().toISOString(),
	};
}

function buildViewerAuthClarificationToolResult(message: string): AiFrontendToolResult {
	return {
		toolId: "address_resolution",
		entityKey: `message:${normalizeEntityAliasInput(message)}`,
		status: "ok",
		displayMode: "hidden",
		summary: "用户没有登录，请登录或者提供账号地址。",
		payload: {
			query: message,
			reason: "viewer_identity_required",
		},
		fetchedAt: new Date().toISOString(),
	};
}

function resolveViewerAddressReference(
	message: string,
	viewerIdentity?: ViewerIdentityLike | null,
): AddressMessageResolution | null {
	if (!SELF_ADDRESS_REFERENCE_RE.test(message)) {
		return null;
	}

	if (viewerIdentity?.isAuthenticated && viewerIdentity.principal) {
		return {
			entity: {
				entityType: "address",
				entityId: viewerIdentity.principal,
				label: viewerIdentity.principal,
				source: "principal",
				matchedText: "viewer:self-reference",
			},
			toolResult: null,
		};
	}

	return {
		entity: null,
		toolResult: buildViewerAuthClarificationToolResult(message),
		needsViewerAuth: true,
	};
}

function resolveAddressMarkFromMessage(message: string): AddressMessageResolution {
	const queries = extractNormalizedAddressMessageQueries(message);
	for (const query of queries) {
		const matches = searchAddressMarksInStorage(query);
		if (matches.length === 0) {
			continue;
		}

		if (matches.length === 1) {
			return {
				entity: toAddressMarkEntity(matches[0]),
				toolResult: null,
			};
		}

		const best = matches[0];
		const second = matches[1];
		const shouldAutoResolve =
			best.matchKind === "exact" &&
			(!second || best.score - second.score >= 120);
		if (shouldAutoResolve) {
			return {
				entity: toAddressMarkEntity(best),
				toolResult: null,
			};
		}

		return {
			entity: null,
			toolResult: buildAddressMarkDisambiguationToolResult(query, matches.slice(0, 8)),
		};
	}

	return {
		entity: null,
		toolResult: null,
	};
}

function toAccountAliasEntity(match: EntityAliasMatch): ResolvedAddressEntity | null {
	if (match.type !== "account") return null;
	return {
		entityType: "address",
		entityId: match.targetId,
		label: match.alias,
		source: "alias-account",
		alias: match.alias,
	};
}

export async function resolveDirectAddressEntityInput(
	input: string,
): Promise<ResolvedAddressEntity | null> {
	const normalized = normalizeEntityAliasInput(input);
	if (!normalized) return null;

	const aliasMatch = resolveEntityAliasExact(normalized);
	const accountAliasEntity = aliasMatch ? toAccountAliasEntity(aliasMatch) : null;
	if (accountAliasEntity) {
		return accountAliasEntity;
	}

	if (PRINCIPAL_REGEX_FLEXIBLE.test(normalized)) {
		return {
			entityType: "address",
			entityId: normalized,
			label: normalized,
			source: "principal",
			matchedText: normalized,
		};
	}

	if (BTC_ADDRESS_REGEX.test(normalized)) {
		const owner = await fetchAddressOwnerByBtcAddress(normalized);
		if (!owner?.id) return null;
		return {
			entityType: "address",
			entityId: owner.id,
			label: owner.id,
			source: "btc-address-owner",
			matchedText: normalized,
		};
	}

	return null;
}

export async function resolveAddressEntityFromMessage(
	message: string,
): Promise<ResolvedAddressEntity | null> {
	const resolution = await resolveAddressEntitySignalFromMessage(message);
	return resolution.entity;
}

export async function resolveAddressEntitySignalFromMessage(
	message: string,
	viewerIdentity?: ViewerIdentityLike | null,
): Promise<AddressMessageResolution> {
	const viewerReferenceResolution = resolveViewerAddressReference(
		message,
		viewerIdentity,
	);
	if (viewerReferenceResolution) {
		return viewerReferenceResolution;
	}

	const containedAlias = findContainedEntityAlias(message, ["account"]);
	const accountAliasEntity = containedAlias
		? toAccountAliasEntity(containedAlias)
		: null;
	if (accountAliasEntity) {
		return {
			entity: accountAliasEntity,
			toolResult: null,
		};
	}

	const principalMatch = extractFirstRegexMatch(message, PRINCIPAL_REGEX_EXTRACT);
	if (principalMatch) {
		return {
			entity: {
				entityType: "address",
				entityId: principalMatch,
				label: principalMatch,
				source: "principal",
				matchedText: principalMatch,
			},
			toolResult: null,
		};
	}

	const btcMatch = extractFirstRegexMatch(message, BTC_ADDRESS_REGEX_EXTRACT);
	if (btcMatch) {
		const owner = await fetchAddressOwnerByBtcAddress(btcMatch);
		if (!owner?.id) {
			return {
				entity: null,
				toolResult: null,
			};
		}
		return {
			entity: {
				entityType: "address",
				entityId: owner.id,
				label: owner.id,
				source: "btc-address-owner",
				matchedText: btcMatch,
			},
			toolResult: null,
		};
	}

	return resolveAddressMarkFromMessage(message);
}
